import * as React from 'react'
import { DataGrid } from '@mui/x-data-grid'
import {
  Box,
  Typography,
  InputBase,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputAdornment,
  InputLabel,
  useTheme,
  useMediaQuery,
  Checkbox,
  ListItemText
} from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import Check from '@mui/icons-material/Check'
import Close from '@mui/icons-material/Close'
import { useState, useEffect } from 'react'
import nodatafound from '../../utilities/dummy_assets/No data-cuate 1.jpg'
import AdminInventoryPurchaseAPIs from '../../utilities/api/inventory/AdminInventoryPurchaseAPIs'
import { useSelector } from 'react-redux'
import CircularProgress from '@mui/material/CircularProgress' // Import CircularProgress
import CustomPagination from '../CustomPagination/CustomPagination'
import { SaveAlt, KeyboardArrowDown } from '@mui/icons-material'

const PurchasesTable = () => {
  const user = useSelector((state) => state.user.user)
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [categories, setCategories] = useState([])
  const [category, setCategory] = useState([])
  const [searchQuery, setSearchQuery] = useState('')
  const [toggleState, setToggleState] = useState({})
  const [pickupStatus, setPickupStatus] = useState([])
  const [selectedPickupStatus, setSelectedPickupStatus] = useState([])

  useEffect(() => {
    const fetchData = async () => {
      try {
        const data = await AdminInventoryPurchaseAPIs.InventoryPurchaseListGet()
        setRows(data.reverse())
        console.log('InventoryPurchaseListGet data', data)

        const uniqueCategories = [...new Set(data.map((item) => item.category))]
        setCategories(uniqueCategories)
        const uniquePickupStatus = [...new Set(data.map((item) => item.pickup_))]
        setPickupStatus(uniquePickupStatus)

        const initialToggleState = data.reduce((acc, item) => {
          if (item !== null && item.hasOwnProperty('id')) {
            acc[item.id] = item.pickup === '0' // Initialize toggle state based on pickup value
          }
          return acc
        }, {})
        setToggleState(initialToggleState)
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user])

  const handleCategoryChange = (event) => {
    setCategory(event.target.value)
  }

  const handlePickupStatusChange = (event) => {
    setSelectedPickupStatus(event.target.value)
  }

  const handleSearchChange = (event) => {
    setSearchQuery(event.target.value)
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault() // Prevent the default action to avoid form submission/reload
    }
  }

  const handleToggleChange = (id, newValue) => async () => {
    setToggleState({
      ...toggleState,
      [id]: newValue
    })

    try {
      console.log('id', id)
      await AdminInventoryPurchaseAPIs.InventoryPurchaseDetailPatch(id, {
        pickup: newValue ? '0' : '1'
      })
    } catch (error) {
      console.error(
        `Error updating detail for ID ${id}:`,
        error.response ? error.response.data : error.message
      )
    }
  }

  const filteredRows = rows.filter((row) => {
    if (row === null) return false
    const purchaseAmount = row.purchase_amount ? row.purchase_amount.toString().toLowerCase() : ''
    const purchaseDatetime = row.purchase_datetime
      ? row.purchase_datetime.toString().toLowerCase()
      : ''
    const purchasedBy = row.purchased_by ? row.purchased_by.toString().toLowerCase() : ''
    const name = row.name ? row.name.toString().toLowerCase() : ''
    const quantity = row.quantity ? row.quantity.toString().toLowerCase() : ''
    const category = row.category ? row.category.toString().toLowerCase() : ''

    const matchesCategory = !category.length || category.includes(row.category)
    const matchesPickupStatus =
      !selectedPickupStatus.length || selectedPickupStatus.includes(row.pickup_)
    const matchesSearchQuery =
      !searchQuery ||
      purchaseAmount.includes(searchQuery.toLowerCase()) ||
      purchaseDatetime.includes(searchQuery.toLowerCase()) ||
      purchasedBy.includes(searchQuery.toLowerCase()) ||
      name.includes(searchQuery.toLowerCase()) ||
      quantity.includes(searchQuery.toLowerCase()) ||
      category.includes(searchQuery.toLowerCase())

    return matchesCategory && matchesPickupStatus && matchesSearchQuery
  })

  const columns = [
    {
      field: 'itemDetails',
      headerName: 'Item Details',
      flex: 1,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'left',
      renderCell: ({ row }) => (
        <Box sx={{ ml: '10%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <img
            src={row.imageUrl || nodatafound} // Provide a default image path if necessary
            alt={row.name || 'No Text'}
            style={{ width: '50px', height: '50px', borderRadius: '50%' }}
          />
          <Box>
            <Typography variant="subtitle2" align="left" fontWeight="normal">
              {row?.name || 'No Text'}
            </Typography>
            <Typography variant="body2" sx={{ color: '#007BFF', fontSize: '12px' }} align="left">
              {row?.project_name || 'No Subtext'}
            </Typography>
          </Box>
        </Box>
      )
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      flex: 0.5,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center' // Center align cell data
    },
    {
      field: 'purchaser',
      headerName: 'Purchased By',
      flex: 1,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center' // Center align cell data
    },
    {
      field: 'purchase_amount',
      headerName: 'Purchase Amount',
      flex: 1,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center' // Center align cell data
    },
    {
      field: 'purchase_datetime',
      headerName: 'Purchase Date',
      flex: 1,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center', // Center align cell data
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="subtitle2" align="left" fontWeight="normal">
            {new Date(row.purchase_datetime).toLocaleDateString('en-GB', {
              day: '2-digit',
              month: 'short',
              year: 'numeric'
            })}
          </Typography>

          <Typography variant="body2" sx={{ fontSize: '12px' }} align="left">
            {new Date(row.purchase_datetime).toLocaleTimeString('en-GB', {
              hour: '2-digit',
              minute: '2-digit',
              hour12: true
            })}
          </Typography>
        </Box>
      )
    },
    {
      field: 'action',
      headerName: 'Picked',
      sortable: false,
      width: 120, // Optional: keep a fixed width for action column if needed
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center', // Center align cell data
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <IconButton
            sx={{ backgroundColor: toggleState[params.row.id] ? '#E5FFF1' : '#F7F7F7' }}
            height="40px"
            width="40px"
            onClick={handleToggleChange(params.row.id, true)}
          >
            <Check sx={{ color: toggleState[params.row.id] ? 'green' : 'grey' }} />
          </IconButton>
          <IconButton
            sx={{ backgroundColor: !toggleState[params.row.id] ? '#FFE5E5' : '#F7F7F7' }}
            height="40px"
            width="40px"
            onClick={handleToggleChange(params.row.id, false)}
          >
            <Close sx={{ color: !toggleState[params.row.id] ? 'red' : 'grey' }} />
          </IconButton>
        </Box>
      )
    },
    {
      field: 'invoice',
      headerName: 'Invoice',
      flex: 1,
      headerClassName: 'header-style',
      headerAlign: 'center', // Center align header
      align: 'center', // Center align cell data
      renderCell: (params) => (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
          <Typography>view</Typography>
          <SaveAlt />
        </Box>
      )
    }
  ]

  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  return (
    <Box sx={{ height: '60%', width: '97.5%', marginLeft: '1rem' }}>
      <Box
        component="form"
        onKeyDown={handleKeyDown}
        sx={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: isMobile ? 1 : 0,
          width: '100%',
          margin: 'none',
          marginBlock: '1rem',
          boxSizing: 'border-box', // Ensure padding does not cause overflow
          marginBottom: '36px'
        }}
      >
        <FormControl sx={{ minWidth: 350 }}>
          <InputLabel>Select Pickup Status</InputLabel>
          <Select
            multiple
            label="Select Pickup Status"
            value={selectedPickupStatus}
            onChange={handlePickupStatusChange}
            renderValue={(selected) => selected.join(', ')}
            IconComponent={KeyboardArrowDown}
            disabled={pickupStatus?.length === 0}
            sx={{
              height: '3rem',
              width: '100%',
              border: '1px solid #F2F2F2',
              textAlign: 'left'
            }}
          >
            {pickupStatus.map((status) => (
              <MenuItem key={status} value={status}>
                <Checkbox checked={selectedPickupStatus.indexOf(status) > -1} />
                <ListItemText primary={status} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <InputBase
          sx={{
            maxWidth: 350,
            ml: 2,
            mr: 1,
            flex: 1,
            height: '3rem',
            border: '1px solid #F2F2F2',
            borderRadius: '10px',
            bgcolor: '#F7F7F7',
            '& .MuiInputBase-input': {
              paddingLeft: '.1rem'
            }
          }}
          placeholder="Search…"
          inputProps={{ 'aria-label': 'search' }}
          value={searchQuery}
          onChange={handleSearchChange}
          startAdornment={
            <InputAdornment position="start">
              <IconButton
                sx={{ paddingLeft: '10px' }} // Adjust padding as needed
                aria-label="toggle password visibility"
              >
                <SearchIcon />
              </IconButton>
            </InputAdornment>
          }
        />
      </Box>
      {loading ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            height: '100%'
          }}
        >
          <CircularProgress
            size={60}
            thickness={4.5}
            sx={{ color: '#273F77' }} // Customize color to match your theme
          />
          <Typography
            variant="h6"
            sx={{ color: '#273F77', fontFamily: 'Inter', fontWeight: 500, marginTop: 2 }}
          >
            Loading Records...
          </Typography>
        </Box>
      ) : filteredRows.length === 0 ? (
        <Box
          sx={{
            display: 'flex',
            marginTop: '100px',
            padding: '32px 24px',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '21px'
          }}
        >
          <Box
            component="img"
            src={nodatafound}
            sx={{
              width: '211.889px',
              height: '196.824px',
              flexShrink: '0'
            }}
          />
          <Typography
            sx={{
              color: 'var(--s-primary-source, #273F77)',
              textAlign: 'center',
              fontFamily: 'Inter',
              fontSize: '26px',
              fontStyle: 'normal',
              fontWeight: '500',
              lineHeight: '150%',
              letterSpacing: '-0.352px'
            }}
          >
            No Records Found!
          </Typography>
        </Box>
      ) : (
        <DataGrid
          rows={filteredRows}
          columns={columns}
          pageSizeOptions={[7, 10, 25]}
          initialState={{
            pagination: { paginationModel: { pageSize: 7 } }
          }}
          pagination
          paginationMode="client"
          disableSelectionOnClick
          disableColumnFilter
          disableColumnMenu
          disableDensitySelector
          disableExport
          disableMultipleColumnsSorting
          sortingOrder={[]}
          disableColumnSort
          slots={{
            pagination: CustomPagination
          }}
          sx={{
            '& .MuiDataGrid-columnHeaders': {
              backgroundColor: '#f7f7f7'
            },
            '& .MuiDataGrid-cell': {
              color: '#898989'
            },
            '& .header-style': {
              fontSize: '1.2rem',
              color: '#5C5C5C'
            },
            '& .MuiDataGrid-selectedRowCount': {
              visibility: 'hidden',
              width: 0,
              padding: 0,
              margin: 0,
              flex: '0 !important'
            },
            '& .MuiDataGrid-footerContainer': {
              paddingTop: '4px',
              paddingBottom: '4px',
              justifyContent: 'center'
            }
          }}
        />
      )}
    </Box>
  )
}

export default PurchasesTable
